import {
    useCallback,
    useEffect,
    useState,
} from 'react'

import { Camera, CameraOff, RefreshCw } from 'lucide-react'

import OpenInBrowserActions from '../../../OpenInBrowserActions'
import {
    checkCameraAccess,
    getClientEnvironment,
    isMobileDevice,
} from '../../../../browserEnvironment'
import trackPreCheckFormProgress from '../../../../services/track-pre-check-form-progress'

const FAILURE_COPY = {
    denied: {
        title: "We couldn't access your camera",
        text: 'Camera access was blocked. This often happens when the link opens inside an email app (like Gmail or Outlook) instead of your browser.',
        showBrowser: true,
        showPermissionTip: true,
    },
    timeout: {
        title: "We couldn't access your camera",
        text: "The camera permission request didn't complete. This often happens when the link opens inside an email app (like Gmail or Outlook) instead of your browser.",
        showBrowser: true,
        showPermissionTip: true,
    },
    unsupported: {
        title: "This screen can't use your camera",
        text: 'The app this page is open in does not allow camera access. Please open this page in your browser to continue.',
        showBrowser: true,
    },
    not_found: {
        title: 'No camera found',
        text: 'We could not find a camera on this device. Please open this link on a phone with a working camera.',
        showBrowser: false,
    },
    in_use: {
        title: 'Your camera is busy',
        text: 'Another app is using your camera. Close other apps that use the camera (video calls, camera app), then tap Try again.',
        showBrowser: false,
    },
    error: {
        title: "We couldn't access your camera",
        text: 'Something went wrong while starting your camera. Please try again, or open this page in your browser.',
        showBrowser: true,
        showPermissionTip: true,
    },
}

// Runs a camera check on phones before the Veriff step is shown. Inside email apps (Gmail's
// Chrome Custom Tab on older Android, Outlook/Facebook WebViews, ...) the camera often fails
// and Veriff leaves the candidate stuck. On failure we explain why and offer to reopen the
// page in the browser. Desktop skips the check: Veriff there offers continuing on the phone.
const CameraAccessGate = ({ enabled, formKey, documentId, children }) => {

    const needsCheck = enabled && isMobileDevice()

    const [state, setState] = useState(needsCheck ? 'checking' : 'ok')
    const [result, setResult] = useState(null)

    const logEvent = useCallback((type, extra) => {
        if (!formKey) return
        trackPreCheckFormProgress({
            key: formKey,
            currentPageType: 'pre_check',
            currentPageDocumentId: documentId,
            clientEnvironment: getClientEnvironment(type, extra),
        })
    }, [formKey, documentId])

    const runCheck = useCallback(async () => {
        setState('checking')
        const check = await checkCameraAccess()
        logEvent('camera_check', { cameraResult: check.result, cameraError: check.errorName })
        setResult(check.result)
        setState(check.result === 'ok' ? 'ok' : 'failed')
    }, [logEvent])

    useEffect(() => {
        if (needsCheck) runCheck()
        // Run once per mount (each document page remounts this).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    if (state === 'ok') return children

    const copy = FAILURE_COPY[result] || FAILURE_COPY.error

    return (
        <div className="flex items-center justify-center px-4 py-10">
            <div className="max-w-xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">
                {state === 'checking' ? (
                    <div className="flex flex-col items-center text-center gap-4 py-6">
                        <Camera className="w-12 h-12 text-blue-600 animate-pulse" />
                        <div className="space-y-1">
                            <h2 className="text-xl font-semibold text-gray-800">Checking camera access</h2>
                            <p className="text-gray-600 text-sm">
                                If you are asked, please tap <strong>Allow</strong> to let this page use your camera.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col items-center text-center gap-3">
                            <CameraOff className="w-12 h-12 text-rose-500" />
                            <h2 className="text-xl font-semibold text-gray-800">{copy.title}</h2>
                            <p className="text-gray-600 text-sm">{copy.text}</p>
                        </div>

                        {copy.showBrowser && (
                            <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                                <p className="font-semibold text-gray-800">Open this page in your browser</p>
                                <OpenInBrowserActions onAction={(type) => logEvent(type)} />
                            </div>
                        )}

                        {copy.showPermissionTip && (
                            <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600 space-y-1">
                                <p className="font-semibold text-gray-700">Already in your browser?</p>
                                <p>
                                    Tap the icon next to the web address, open <strong>Permissions</strong> (or
                                    {' '}<strong>Site settings</strong>), set <strong>Camera</strong> to <strong>Allow</strong>,
                                    then tap Try again. If the browser itself has no camera permission, allow it in your
                                    phone's <strong>Settings → Apps</strong>.
                                </p>
                            </div>
                        )}

                        <div className="flex flex-col items-center gap-3">
                            <button
                                type="button"
                                onClick={runCheck}
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gray-800 hover:bg-gray-900 text-white font-semibold transition-all"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Try again
                            </button>
                            <button
                                type="button"
                                onClick={() => setState('ok')}
                                className="text-sm text-gray-500 underline hover:text-gray-700"
                            >
                                Continue anyway
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default CameraAccessGate
